import {
  approveKyc,
  getKycById,
  getKycProcesses,
  getKycStats,
  rejectKyc,
  updateKyc,
  type KycFilters,
  type KycUpdateBody,
} from "@/services/kyc.service";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const KYC_KEY = ["kyc"] as const;
const KYC_STATS_KEY = ["kyc", "stats"] as const;

export const useKycProcesses = (filters: KycFilters = {}) =>
  useQuery({
    queryKey: [...KYC_KEY, filters],
    queryFn: async () => {
      const result = await getKycProcesses(filters);
      if (!result.ok) throw new Error(result.error);
      return result.data.data;
    },
  });

export const useKycProcess = (id: string) =>
  useQuery({
    queryKey: [...KYC_KEY, id],
    queryFn: async () => {
      const result = await getKycById(id);
      if (!result.ok) throw new Error(result.error);
      return result.data.data;
    },
    enabled: !!id,
  });

export const useKycStats = () =>
  useQuery({
    queryKey: KYC_STATS_KEY,
    queryFn: async () => {
      const result = await getKycStats();
      if (!result.ok) throw new Error(result.error);
      return result.data.data;
    },
  });

export const useApproveKyc = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveKyc(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: KYC_KEY});
    },
  });
};

export const useRejectKyc = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, reason}: {id: string; reason: string}) => rejectKyc(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: KYC_KEY});
    },
  });
};

export const useUpdateKyc = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, body}: {id: string; body: KycUpdateBody}) => updateKyc(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: KYC_KEY});
    },
  });
};
